import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const benefits = [
  {
    icon: 'ri-shield-check-line',
    title: 'Storm Protection',
    description: 'Advanced roofing systems designed to withstand severe weather, high winds, and heavy precipitation.'
  },
  {
    icon: 'ri-temp-cold-line',
    title: 'Energy Efficiency',
    description: 'Reflective roofing materials and proper ventilation reduce cooling costs and improve home comfort.'
  },
  {
    icon: 'ri-home-heart-line',
    title: 'Increased Property Value',
    description: 'A new quality roof significantly boosts curb appeal and overall property market value.'
  },
  {
    icon: 'ri-time-line',
    title: 'Long-Term Durability',
    description: 'Premium materials and expert installation ensure decades of reliable protection for your investment.'
  }
];

const warrantyInfo = [
  {
    type: 'Material Warranty',
    coverage: '20-50 Years',
    description: 'Manufacturer warranty covering defects in roofing materials',
    icon: 'ri-file-shield-line'
  },
  {
    type: 'Workmanship Warranty',
    coverage: '10 Years',
    description: 'Our guarantee on installation quality and craftsmanship',
    icon: 'ri-tools-line'
  },
  {
    type: 'Leak Protection',
    coverage: 'Lifetime',
    description: 'Comprehensive leak protection for the life of your roof',
    icon: 'ri-drop-line'
  }
];

export default function RoofingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-4">
                <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                  Professional Roofing Services
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Expert Roofing Solutions
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Protect your investment with premium roofing installation, repair, and replacement services. Our certified roofing specialists deliver quality craftsmanship backed by comprehensive warranties.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="px-8 py-4 bg-brand text-white rounded-lg font-medium hover:bg-brand-dark transition-colors shadow-lg hover:shadow-xl whitespace-nowrap"
                  onClick={(e) => {
                    e.preventDefault();
                    window.REACT_APP_NAVIGATE('/');
                    setTimeout(() => {
                      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                >
                  Get Free Estimate
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/3e92e3fde02231b3177f0502ff406c01.png"
                alt="Professional Roofing Installation"
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                Why Choose Us
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Roofing Benefits
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Invest in a quality roof that protects your home and enhances its value for decades to come
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-5xl text-brand`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warranty Information */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                Peace of Mind
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Warranty Coverage
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your investment is protected with industry-leading warranties covering both materials and workmanship
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {warrantyInfo.map((warranty, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-brand/10 to-white rounded-2xl p-10 border-2 border-brand/20 hover:border-brand/50 transition-all duration-300 hover:shadow-xl cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className={`${warranty.icon} text-5xl text-brand`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {warranty.type}
                </h3>
                <div className="text-3xl font-bold text-brand mb-4">
                  {warranty.coverage}
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {warranty.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gray-50 rounded-2xl p-8 text-center">
            <p className="text-gray-700 text-lg">
              <strong className="text-gray-900">Note:</strong> Warranty terms vary by material and manufacturer. Contact us for detailed warranty information specific to your roofing project.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" style={{backgroundImage: 'url(https://public.readdy.ai/ai/img_res/3f334b1d39de7a92aaa5b53d60336a86.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready for a New Roof?
          </h2>
          <p className="text-xl text-white/80 mb-10 leading-relaxed">
            Get a free, no-obligation estimate for your roofing project. Our experts will assess your needs and provide a detailed quote with material options and warranty information.
          </p>
          <a
            href="#contact"
            className="inline-block px-10 py-5 bg-white text-brand rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors shadow-xl hover:shadow-2xl whitespace-nowrap"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE('/');
              setTimeout(() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
          >
            Request Free Estimate
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}